import { Assets } from 'pixi.js';
import type { AssetsBundle, AssetsManifest } from 'pixi.js';
import last from 'lodash/last';
import cloneDeep from 'lodash/cloneDeep';
import assetManifest from '../assets-manifest.json';

type DeepReadonly<T> = {
  readonly [P in keyof T]: DeepReadonly<T[P]>;
};

interface LayoutCtr {
  raw: {
    list: {
      name: string;
      texture?: string;
      stateByName?: Record<string, { texture?: string }>;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      data?: { bundlesToPreload?: Record<string, AssetsBundle['assets']>; DEBUG?: boolean };
    }[];
    animations?: { blocks: { target: { textureName?: string } }[] }[];
  };
}

export type LayoutConstructor = DeepReadonly<LayoutCtr>;

type LookUpMap = Record<string, { assetToLoad: string; width: number; height: number; isAtlas: boolean }>;

function createLookUpMap(): LookUpMap {
  const output: LookUpMap = {};

  (assetManifest as AssetsManifest).bundles.forEach((bundle) => {
    if (!Array.isArray(bundle.assets)) return;

    bundle.assets.forEach((asset) => {
      const assetData = asset.data as {
        texturesInAtlas?: Record<string, { w: number; h: number }>;
        imageSize?: { w: number; h: number };
      };

      if (assetData.texturesInAtlas) {
        Object.entries(assetData.texturesInAtlas).forEach(([textureName, data]) => {
          output[textureName] = { assetToLoad: last(asset.alias) ?? '', width: data.w, height: data.h, isAtlas: true };
        });
      }

      if (assetData.imageSize) {
        const key = last(asset.alias) ?? '';
        output[key] = {
          assetToLoad: key ?? '',
          width: assetData.imageSize.w,
          height: assetData.imageSize.h,
          isAtlas: false,
        };
      }
    });
  });

  return output;
}

const assetLookUpMap = createLookUpMap();

function getTexturesFromLayouts(layouts: LayoutConstructor[]): Set<string> {
  const usedTextures = new Set<string>();

  layouts.forEach((layout) => {
    layout.raw.list.forEach((model) => {
      if (model?.data?.DEBUG) return;
      if (model.texture) usedTextures.add(model.texture);
      if (model.stateByName) {
        Object.values(model.stateByName).forEach((state) => {
          if (state.texture) usedTextures.add(state.texture);
        });
      }
    });
    layout.raw.animations?.forEach((animation) => {
      animation.blocks.forEach((block) => {
        if (block?.target?.textureName) usedTextures.add(block.target.textureName);
      });
    });
  });

  return usedTextures;
}

export type LayoutAssetReport = {
  texturesToAtlas: Record<string, string>;
  atlasesUse: Record<string, number>;
  unknowns: string[];
  singleTextures: string[];
};

export function getLayoutAssetsReport(layout: LayoutConstructor): LayoutAssetReport {
  const report: LayoutAssetReport = { texturesToAtlas: {}, atlasesUse: {}, unknowns: [], singleTextures: [] };

  getTexturesFromLayouts([layout]).forEach((texture) => {
    const asset = assetLookUpMap[texture];

    if (!asset) {
      report.unknowns.push(texture);
      return;
    }

    if (!asset.isAtlas) {
      report.singleTextures.push(texture);
      return;
    }

    report.texturesToAtlas[texture] = asset.assetToLoad;
    if (!report.atlasesUse[asset.assetToLoad]) {
      report.atlasesUse[asset.assetToLoad] = 1;
    } else {
      report.atlasesUse[asset.assetToLoad] += 1;
    }
  });

  return report;
}

// TODO nested assets are not handled
export function getAssetsToPreload(layouts: LayoutConstructor[]): {
  bundles: Record<string, AssetsBundle['assets']>;
  assetsToPreload: (string | string[])[];
  missingTextures: string[];
} {
  const assetsToPreload = new Set<string>();
  const textures = new Set<string>();

  [...getTexturesFromLayouts(layouts)].forEach((texture) => {
    if (assetLookUpMap[texture] && !Assets.cache.has(assetLookUpMap[texture].assetToLoad)) {
      assetsToPreload.add(assetLookUpMap[texture].assetToLoad);
      textures.add(texture);
    }
  });

  const bundles: Record<string, AssetsBundle['assets']> = {};

  layouts.forEach((layout) => {
    const root = layout.raw.list.find((model) => model.name === 'root');

    const rawBundles = root?.data?.bundlesToPreload as unknown as Record<string, AssetsBundle['assets']> | undefined;

    Object.entries(rawBundles ?? {}).forEach(([bundleId, assets]) => {
      // clone to ensure mutability regardless of DeepReadonly source type
      bundles[bundleId] = cloneDeep(assets);
    });
  });

  return {
    bundles,
    assetsToPreload: [...assetsToPreload],
    missingTextures: [...textures],
  };
}

export function getTexturePreloadData(
  textureName: string,
): { assetToLoad: string; width: number; height: number } | null {
  return assetLookUpMap[textureName];
}
